import { html, LitElement, TemplateResult, CSSResult, css } from 'lit';
import {property, customElement, state} from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map';
import { repeat } from 'lit/directives/repeat.js';
import { Site } from 'src/objectModelV2/site';
import { store } from '../redux/store';
import { AirCurtain } from 'src/objectModelV2/airCurtain';
import { EVENTS, loadStyle, orderAirCurtains, getTimeElapsed } from '../api/ui';

@customElement('berner-curtains')
export class BernerCurtains extends LitElement {
    // if set only show aircurtains for the site in question
    @property({ attribute: 'selected-site' })
    private selectedSite = '';

    private _sites: Site[] = [];
    private _airCurtainsBySite: Map<string, AirCurtain[]> = new Map();
    private _allAirCurtains: {curtain: AirCurtain, site: string}[] = [];
    private _lessThan480px = false;
    
    @state()
    private _initialized = false;

    @property({ attribute: 'selected-air-curtain' })
    private _selectedAirCurtain = '';
    @state()
    get selectedAirCurtain(): string {
        return this._selectedAirCurtain.toString();
    }

    set selectedAirCurtain(value: string) {
        this._selectedAirCurtain = value;
    }

    // class css-loading is removed as soon as the external css is loaded
    public static get styles(): CSSResult {
        return css`
            .css-loading {
                display: none;
            }
        `;
    }

    constructor() {
        super();
        this.init();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener('resize', this.onResize);
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        window.removeEventListener('resize', this.onResize);
    }

    init = async (): Promise<void> => {
        const { objectManager } = store.getState();
        const siteGroup = objectManager.getSiteGroup();

        for (const siteId of siteGroup.sites) {
            const site = objectManager.getSite(siteId);
            this._sites.push(site);
            const siteCurtainsMap: Map<string, AirCurtain[]> = new Map();

            for (const curtainGroupId of site.airCurtainGroups) {
                const airCurtainGroup = objectManager.getAirCurtainGroup(
                    curtainGroupId
                );
                const groupAirCurtains = await objectManager.getAllAirCurtainsFromAirCurtainGroup(
                    airCurtainGroup
                );
                let siteCurtains = siteCurtainsMap.get(siteId) ?? [];
                siteCurtains = siteCurtains.concat(groupAirCurtains);
                siteCurtainsMap.set(siteId, siteCurtains);
            }

            const orderedSiteCurtains = orderAirCurtains(siteCurtainsMap);
            const siteCurtains = orderedSiteCurtains?.map((obj) => {return obj.curtain});
            this._airCurtainsBySite.set(site.siteID, siteCurtains);
        }

        this._allAirCurtains = orderAirCurtains(this._airCurtainsBySite);

        this._initialized = true;
    }

    firstUpdated = async (): Promise<void> => {
        await loadStyle('./css/berner-curtains.css', this.shadowRoot);

        if (window.matchMedia('(max-width: 479.98px)').matches) {
            this._lessThan480px = true;
        } else {
            this._lessThan480px = false; 
        }

        this.requestUpdate();
    };

    onResize = async (): Promise<void> => {
        const wasLessThan480px = this._lessThan480px;
        if (window.matchMedia('(max-width: 479.98px)').matches) {
            this._lessThan480px = true;
        } else {
            this._lessThan480px = false; 
        }

        if (wasLessThan480px !== this._lessThan480px) {
            this.requestUpdate();
        }
    };

    onClickCurtain = (airCurtainId: string, siteId: string): void => {
        if (airCurtainId === this.selectedAirCurtain) {
            this.selectedAirCurtain = '';
        } else {
            this.selectedAirCurtain = airCurtainId;
        }

        const event = new CustomEvent(EVENTS.CURTAIN_SELECTED, {
            detail: { curtain: this.selectedAirCurtain, site: siteId },
        });
        window.dispatchEvent(event);
    };

    renderNoAirCurtains(): TemplateResult {
        return html`
            <div class="col-12">
                <p class="mx-auto pt-3 my-auto label">
                    This site does not have any air curtains yet.
                </p>
            </div>
        `;
    }

    renderAirCurtain(curtain: AirCurtain, siteId = ''): TemplateResult {
        const connectivityPrescript = curtain?.connected ? 'Connected: ' : 'Online: ';
        const connectivityMessage = curtain?.connectivityTimestamp ? connectivityPrescript + getTimeElapsed(curtain?.connectivityTimestamp) : ''

        return html`
        <div class=${classMap({"row": true, "py-3": true, "air-curtain": true, "selected": this.selectedAirCurtain === curtain.airCurtainID})} @click=${(e: Event) => {
            this.onClickCurtain(curtain.airCurtainID, siteId);
            e.stopPropagation();
        }}>
            <span class='col-1'>
                <span class=${classMap({'dot': curtain?.connected, 'dot--red': !curtain?.connected })}></span>
            </span>
            ${curtain.connectivityTimestamp && !this._lessThan480px ? 
                html`
                <span class="col-5 curtain-name"> ${curtain?.name}</span>
                <span class="col-5 curtain-last-online">${connectivityMessage}</span>`
                : html`<span class=${classMap({"curtain-name": true, "col-11": !this._lessThan480px, "col-10": this._lessThan480px})}> ${curtain?.name}</span>`
            }
        </div>`
    }

    renderSiteCurtains(siteId: string): TemplateResult {
        const siteAirCurtains = this._airCurtainsBySite.get(siteId);

        if (!siteAirCurtains.length) {
            return html`${this.renderNoAirCurtains()}`;
        }

        return html`
        ${repeat(
            siteAirCurtains,
            (curtain: AirCurtain) => {
                return html`${this.renderAirCurtain(curtain, siteId)}`
            })}`;
    }

    renderAllAirCurtains(): TemplateResult {
        if (!this._allAirCurtains.length) return html``;

        return html`
            <ul class="curtains-list p-0 content">
                ${repeat(this._allAirCurtains, (obj: {curtain: AirCurtain, site: string}) => {
                    return html` ${this.renderAirCurtain(obj.curtain, obj.site)} `;
                })}
            </ul>
        `;
    }

    renderAirCurtains(): TemplateResult {
        if (!this._airCurtainsBySite.size) return html``;

        if (this.selectedSite) {
            return html`
                <ul class="curtains-list p-0 content">
                    ${this.renderSiteCurtains(this.selectedSite)}
                </ul>`;
        } else {
            return html`${this.renderAllAirCurtains()}`;
        }
    }

    render(): TemplateResult {
        return html`
        <link id="css-target">
        <div class="css-loading" id="root">
            <div class="col-lg-12 p-0">
                ${this._initialized ? this.renderAirCurtains() : html``}
            </div>
        </div>`;
    }
}
